import React from 'react';
import {
	WhatsappShareButton,
	TelegramShareButton,
	VKShareButton,
	TwitterShareButton,
	RedditShareButton,
} from 'react-share';
import Icon, { IconColor } from '@/components/atoms/UI/Icon/Icon';
import FlexContainer from '@/components/molecules/Layout/FlexContainer';
import styled from 'styled-components';
import { transition } from '@/styles/template';
import { getAppLocale } from '@/core/utils/i18nHelper';

interface Props {
	url: string;
	/**
	 * Аудитория определяет набор кнопок:
	 * - 'ru' → WhatsApp, Telegram, VK, MAX, Twitter
	 * - 'en' (любой язык, кроме русского) → WhatsApp, Telegram, Twitter, Reddit
	 * По умолчанию — getAppLocale() по `window.App.locale`, fallback — 'ru'.
	 */
	locale?: 'ru' | 'en';
}

const StyledFlexContainer = styled(FlexContainer)`
  & > a {
    transition: ${transition.fastEnd};
  }

  & > button:hover {
    opacity: .7;
  }
`;

/** Кнопка «поделиться» для мессенджера MAX (в react-share его нет). */
const MaxShareButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
  display: inline-flex;
`;

const ShareButtons: React.FC<Props> = ({ url, locale }) => {
	const size: number = 4;
	const color: IconColor = "default";

	const resolvedLocale = locale ?? getAppLocale();
	const isEnglishLocale = resolvedLocale === 'en';

	const shareToMax = () => {
		const shareUrl = `https://max.ru/:share?text=${encodeURIComponent(url)}`;
		window.open(shareUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<StyledFlexContainer gap="2" centeredContentMobileOnly>
			{/* WhatsApp */}
			<WhatsappShareButton url={url}>
				<Icon name={'WhatsApp'} size={size} color={color} />
			</WhatsappShareButton>

			{/* Telegram */}
			<TelegramShareButton url={url}>
				<Icon name={'Telegram'} size={size} color={color} />
			</TelegramShareButton>

			{/* VK — только русский сайт */}
			{!isEnglishLocale && (
				<VKShareButton url={url}>
					<Icon name={'VK'} size={size} color={color} />
				</VKShareButton>
			)}

			{/* MAX — только русский сайт */}
			{!isEnglishLocale && (
				<MaxShareButton type="button" onClick={shareToMax} aria-label="MAX">
					<Icon name={'Max'} size={size} color={color} />
				</MaxShareButton>
			)}

			{/* Twitter/X */}
			<TwitterShareButton url={url}>
				<Icon name={'Twitter'} size={size} color={color} />
			</TwitterShareButton>

			{/* Reddit — все языки, кроме русского */}
			{isEnglishLocale && (
				<RedditShareButton url={url}>
					<Icon name={'Reddit'} size={size} color={color} />
				</RedditShareButton>
			)}
		</StyledFlexContainer>

	);
};

export default ShareButtons;
